'use client'

import {
  AppInsightsProvider,
  recordErrorInAppInsights,
} from '@dxp/components/app-insights/client'
import { GlobalError } from '@dxp/components/error'
import { ButtonBase, Link } from '@mui/material'
import { AppRouterCacheProvider } from '@mui/material-nextjs/v15-appRouter'
import { SessionProvider, signOut, useSession } from 'next-auth/react'
import { useEffect, useState } from 'react'

import { getAppInsightsConnectionString } from '../actions/get-app-insights-connection-string'
import { logError } from '../actions/log-error'
import { ThemeRegistry } from './client-providers'
import { bodyFont } from './font'

/**
 * A session is considered expired when it can no longer be refreshed or has
 * outlived its max duration, or when its tokens have lapsed without the error
 * flag having been set yet (e.g. a server render threw a 401 before the session
 * poll surfaced the error). In all of these cases the user must be signed out and
 * sent to the sign-out page rather than left on the technical error page.
 */
export default function GlobalErrorPage({
  error,
}: {
  error: Error & { digest?: string }
}) {
  return (
    <SessionProvider>
      <GlobalErrorComponent error={error} />
    </SessionProvider>
  )
}

function GlobalErrorComponent({
  error,
}: {
  error: Error & { digest?: string }
}) {
  const { data: session } = useSession()
  const [appInsightsConnectionString, setAppInsightsConnectionString] =
    useState<string>()

  // global-error replaces the root layout so cannot receive server config as a prop
  useEffect(() => {
    getAppInsightsConnectionString()
      .then(connectionString =>
        setAppInsightsConnectionString(connectionString ?? undefined),
      )
      .catch(err =>
        console.error('Failed to load App Insights connection string:', err),
      )
  }, [])

  const handleSignOutError = (err: unknown) => {
    console.error('Signout failed:', err)
    window.location.href = '/api/auth/signin'
  }

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') {
      // only signout once we have a session
      if (session?.error) {
        signOut({
          redirect: true,
          callbackUrl: session?.logoutUrl ?? '/signed-out',
        }).catch(handleSignOutError)
      }

      logError(error.message)
    }
    // oxlint-disable-next-line react/exhaustive-deps
  }, [error, session?.error, session?.expiresAt, session?.terminateAt])

  // Runs after AppInsightsProvider (a child) has initialised with the loaded connection string
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' || !appInsightsConnectionString)
      return

    const appInsightsErrorProperties: {
      qcId?: string
      GLOBAL_UI_ERROR: true
    } = {
      GLOBAL_UI_ERROR: true,
    }

    if (session?.error) {
      appInsightsErrorProperties.qcId = session.qcId
    }

    recordErrorInAppInsights(error, undefined, appInsightsErrorProperties)
    // oxlint-disable-next-line react/exhaustive-deps
  }, [error, appInsightsConnectionString])

  return (
    <html lang="en" className={bodyFont.variable}>
      <body>
        <AppInsightsProvider
          appId="dxp-customer-portal-web-client"
          connectionString={appInsightsConnectionString}
        >
          <AppRouterCacheProvider options={{ enableCssLayer: true }}>
            <ThemeRegistry>
              <main>
                <GlobalError>
                  <p>
                    We are sorry, but there seems to be a technical error which
                    is preventing this page from loading.
                  </p>
                  <p>
                    Please ensure that your device is connected to the internet,
                    and try{' '}
                    <ButtonBase
                      sx={{
                        fontSize: 'inherit',
                        fontFamily: 'inherit',
                        color: 'inherit',
                        textDecoration: 'underline',
                      }}
                      onClick={() => window.location.reload()}
                    >
                      refreshing the page
                    </ButtonBase>
                    .
                  </p>
                  <p>
                    However, should this error persist, please try again later
                    or{' '}
                    <Link
                      color="inherit"
                      href="https://www.quiltercheviot.com/contact-us"
                      underline="none"
                    >
                      contact us
                    </Link>{' '}
                    for more assistance.
                  </p>
                  {error.digest && <p>Digest: {error.digest}</p>}
                </GlobalError>
              </main>
            </ThemeRegistry>
          </AppRouterCacheProvider>
        </AppInsightsProvider>
      </body>
    </html>
  )
}
